'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Component } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { UserTrackStatuses } from '../../constants/TracksConstants';
import { getFirstContentForTrackUrl, getNextContentUrl, getTrack, trackIsLoading } from '../../selectors/trackSelectors';
import { getLesson } from '../../selectors/lessonSelectors';
import MissingContentError from '../../components/Errors/MissingContentError';
import TrackPage from '../../components/TrackPage/TrackPage';
import ApiError from '../../components/Errors/ApiError';
import UsageTracker from '../../utils/UsageTracker';
import { getContentName } from '../../selectors/contentSelectors';
import { SCREENS } from '../../constants/Tracking';
import { getTrackExamPageUrl, getTrackIntroPageUrl } from '../../utils/RoutingUtils';
import TrackDeprecatedModal from '../../components/Modals/FeatureIntroModals/TrackDeprecatedModal';
import { useAssetStatus, useTrackProgressById, useTrackRedirectUrl } from '../../features/userProgress';

class TrackPageContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mostRecentContentIdTracked: undefined
    };
    this.handleAdvanceContent = this.handleAdvanceContent.bind(this);
  }

  componentDidMount() {
    this.maybeRedirectAndTrack();
  }

  componentDidUpdate() {
    this.maybeRedirectAndTrack();
  }

  maybeRedirectAndTrack() {
    this.maybeRedirectToFirstUnprogressedContent();
    this.maybeTrackPageView();
  }

  maybeRedirectToFirstUnprogressedContent() {
    const {
      track,
      history: {
        replace,
        location
      },
      match: {
        params: {
          contentId,
          lessonId
        }
      },
      lesson,
      firstUnprogressedContentUrl,
      firstUnprogressedContentUrlLoading,
      userTrackStatus,
      firstContentUrl,
      progressIsLoading
    } = this.props;

    if (!firstUnprogressedContentUrlLoading && !progressIsLoading && track && !track.get('isRequesting') && !track.get('hasError')) {
      const userHasntSetLessonOrContent = !lessonId && !contentId;
      let pathname = false;

      if (userHasntSetLessonOrContent) {
        if (userTrackStatus === UserTrackStatuses.COMPLETED && track.get('examId')) {
          pathname = getTrackExamPageUrl(track.get('id'));
        } else if ((userTrackStatus === UserTrackStatuses.NOT_STARTED || userTrackStatus === UserTrackStatuses.COMPLETED) && (track.get('introVideoId') || track.get('introVideo'))) {
          pathname = getTrackIntroPageUrl(track.get('id'));
        } else if (firstUnprogressedContentUrl) {
          pathname = firstUnprogressedContentUrl;
        } else {
          pathname = firstContentUrl;
        }
      } else if (lesson && !(contentId && lesson.get('contentIds').includes(contentId))) {
        pathname = `/tracks/${track.get('id')}/${lessonId}/${lesson.get('contentIds').first()}`;
      }

      if (pathname) {
        replace(Object.assign({}, location, {
          pathname
        }));
      }
    }
  }

  maybeTrackPageView() {
    const {
      contentName,
      match: {
        params: {
          contentId,
          lessonId,
          trackId
        }
      },
      track,
      lesson
    } = this.props;
    const trackName = track && track.get('name');

    if (trackName && contentName && contentId && contentId !== this.state.mostRecentContentIdTracked) {
      const lessonName = lesson && lesson.get('name');
      UsageTracker.track('subpageView', {
        screen: SCREENS.TRACK,
        subscreen: contentName,
        contentId,
        lessonId,
        trackId,
        trackName,
        lessonName,
        hsSoftwareFilters: track.get('hsSoftwareFilters').join(', '),
        allCategories: track.get('subCategories').join(', ')
      });
      this.setState({
        mostRecentContentIdTracked: contentId
      });
    }
  }

  handleAdvanceContent() {
    const {
      history: {
        push,
        location
      },
      nextContentUrl
    } = this.props;
    push(Object.assign({}, location, {
      pathname: nextContentUrl
    }));
  }

  render() {
    const {
      lesson,
      track,
      isLoading,
      match: {
        params: {
          contentId,
          lessonId
        }
      },
      progressIsLoading
    } = this.props;
    const trackHasReturnedWithError = !isLoading && track.get('hasError');
    const lessonForIdDoesntExist = !isLoading && lessonId && !lesson;

    if (trackHasReturnedWithError) {
      return /*#__PURE__*/_jsx(ApiError, {
        className: "track-api-error",
        error: track.get('recentError'),
        marker404: "COURSE_VIEW_API_404",
        markerDefault: "COURSE_VIEW_API_ERROR"
      });
    } else if (lessonForIdDoesntExist) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "COURSE_VIEW_CONTENT_MISSING_ERROR",
        children: /*#__PURE__*/_jsx(MissingContentError, {
          className: "track-api-error"
        })
      });
    } else if (progressIsLoading || !track || (track.get('isRequesting') || isLoading) && !track.get('name') || !contentId || !lessonId || !lesson) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        "data-test-id": "track-page-container-loader",
        grow: true,
        minHeight: 200
      });
    }

    return /*#__PURE__*/_jsxs(NavMarker, {
      name: "COURSE_VIEW_LOADED",
      children: [/*#__PURE__*/_jsx(TrackPage, {
        contentId: contentId,
        lesson: lesson,
        onAdvanceContent: this.handleAdvanceContent,
        track: track
      }), track.get('deprecated') && /*#__PURE__*/_jsx(TrackDeprecatedModal, {
        cta: track.get('deprecationCta'),
        message: track.get('deprecationMessage'),
        trackId: track.get('id')
      })]
    });
  }

}

TrackPageContainer.propTypes = {
  contentName: PropTypes.string,
  firstContentUrl: PropTypes.string,
  firstUnprogressedContentUrl: PropTypes.string,
  firstUnprogressedContentUrlLoading: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string
    }).isRequired
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  lesson: ImmutablePropTypes.map,
  match: PropTypes.shape({
    params: PropTypes.shape({
      trackId: PropTypes.string,
      contentId: PropTypes.string,
      lessonId: PropTypes.string
    }).isRequired
  }).isRequired,
  nextContentUrl: PropTypes.string,
  progressIsLoading: PropTypes.bool.isRequired,
  track: ImmutablePropTypes.map,
  userTrackStatus: PropTypes.oneOf(Object.keys(UserTrackStatuses))
};

function mapStateToProps(state, ownProps) {
  const {
    contentId,
    lessonId,
    trackId
  } = ownProps;
  const isLoading = trackIsLoading(state, {
    trackId
  });
  return {
    contentName: getContentName(state, contentId),
    lessonId,
    lesson: getLesson(state, {
      lessonId
    }),
    track: getTrack(state, {
      trackId
    }),
    isLoading,
    nextContentUrl: getNextContentUrl(state, {
      contentId,
      lessonId,
      trackId
    }),
    firstContentUrl: getFirstContentForTrackUrl(state, {
      trackId
    })
  };
}

const WithProgressTrackPageContainer = (_ref) => {
  let {
    trackId,
    language
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["trackId", "language"]);

  const {
    trackProgressByIdLoading
  } = useTrackProgressById(Number(trackId));
  const {
    status,
    allProgressLoading
  } = useAssetStatus({
    id: Number(trackId),
    type: 'TRACK'
  });
  const {
    trackRedirectUrl,
    trackRedirectUrlLoading
  } = useTrackRedirectUrl(trackId, language);
  return /*#__PURE__*/_jsx(TrackPageContainer, Object.assign({
    firstUnprogressedContentUrl: trackRedirectUrl,
    firstUnprogressedContentUrlLoading: trackRedirectUrlLoading,
    language: language,
    progressIsLoading: allProgressLoading || trackProgressByIdLoading,
    trackId: trackId,
    userTrackStatus: status
  }, props));
};

export default withRouter(connect(mapStateToProps)(WithProgressTrackPageContainer));